import React, { useCallback, useContext, useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { ReactComponent as CheckIcon } from '../../../../../../assets/icons/v3/check-circle-v2.svg';
import { ReactComponent as ClockIcon } from '../../../../../../assets/icons/v3/clock.svg';
import BdsButton from '../../../../../../shared/components/v3/Buttons/BdsButton/BdsButton';
import { XERROR_ICON } from '../../../../../../shared/constants/images';
import { FLEXIBLE_REMUNERATION_REQUEST_STATUS } from '../../../../../../shared/constants/requests';
import { CompanyContext } from '../../../../../../shared/context/company/companyContext';
import { PlansContext } from '../../../../../../shared/context/plans/plansContext';
import { IFlexibleRemunerationRequest } from '../../../../../../shared/interfaces/company';
import { TRANSLATIONS_REQUESTS } from '../../../../../../shared/translations/requests';
import { GenericVoidFunction } from '../../../../../../shared/types/genericTypes';

import './CourseRequestDiscounts.scss';

interface ICourseRequestDiscountsProps {
  request: IFlexibleRemunerationRequest;
  onCancel: GenericVoidFunction;
}

const CourseRequestDiscounts = ({ request, onCancel }: ICourseRequestDiscountsProps) => {
  const { t } = useTranslation();
  const { companyRFCycles, locale: companyLocale } = useContext(CompanyContext);
  const { modelType } = useContext(PlansContext);

  const getLocalizedMonthLabel = (raw: string): string => {
    if (!/^\d{4}-\d{2}$/.test(raw)) return raw;
    const [year, mm] = raw.split('-');
    const date = new Date(Number(year), Number(mm) - 1, 1);
    const rawName = date.toLocaleString(companyLocale || 'en-US', { month: 'long' }).replace('.', '');
    return rawName.charAt(0).toUpperCase() + rawName.slice(1);
  };

  const openCycleMonth = useMemo(() => {
    const open = companyRFCycles?.payRollCycles?.find((c) => c.status === 'OPEN');
    return open ? `${open.cycleYear}-${String(open.cycleMonth).padStart(2, '0')}` : undefined;
  }, [companyRFCycles]);

  const paymentsForDisplay = useMemo(() => {
    const today = new Date();
    const base = (request.details.payments || [])
      .filter((payment) => /^\d{4}-\d{2}$/.test(payment.month))
      .map((payment) => {
        const [yearStr, monthStr] = payment.month.split('-');

        const monthOffset = modelType === 'forecast' ? -1 : 0;
        const date = new Date(Number(yearStr), Number(monthStr) - 1 + monthOffset, 1);

        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const displayMonthStr = String(month).padStart(2, '0');
        const displayMonth = `${year}-${displayMonthStr}`;

        const isFuture = date > today;
        const isOpenCycle = openCycleMonth === displayMonth;
        const displayStatus = !isFuture && !isOpenCycle ? 'paid' : 'toPay';
        return { month: displayMonth, amount: payment.amount, displayStatus };
      })
      .sort((a, b) => (a.month < b.month ? -1 : 1));

    if (request.status === FLEXIBLE_REMUNERATION_REQUEST_STATUS.cancelled.value) {
      return base.filter((p) => p.displayStatus === 'paid');
    }
    return base;
  }, [request.details.payments, openCycleMonth, request.status, modelType]);

  const formatCurrency = (amount: number): string => {
    try {
      return new Intl.NumberFormat(companyLocale || 'es-ES', {
        style: 'currency',
        currency: request.currency?.toUpperCase() === 'EUR' ? 'EUR' : request.currency?.toUpperCase() || 'EUR',
        maximumFractionDigits: 2
      }).format(amount);
    } catch {
      return `${amount} ${request.currency?.toUpperCase() || ''}`.trim();
    }
  };

  const fillRows = useCallback(() => {
    const columns = 3;
    const remainder = paymentsForDisplay.length % columns;
    const needsFiller = paymentsForDisplay.length > columns && remainder !== 0;
    if (!needsFiller) return null;
    const span = remainder === 1 ? columns - 1 : 1;
    return <div className="crd-payment-filler" style={{ gridColumn: `span ${span}` }} aria-hidden="true" />;
  }, [paymentsForDisplay]);

  return (
    <div className="flex flex-col gap-11" data-experience="new">
      <div className="course-request-details">
        <div className="crd-title">{t(TRANSLATIONS_REQUESTS.details.deductionsCard.title)}</div>
        <div className="flex flex-col gap-1">
          <p className="label">{t(TRANSLATIONS_REQUESTS.details.deductionsCard.totalAmount)}</p>
          <p className="value">{formatCurrency(request.amount)}</p>
        </div>
        {(request.status === FLEXIBLE_REMUNERATION_REQUEST_STATUS.active.value ||
          request.status === FLEXIBLE_REMUNERATION_REQUEST_STATUS.cancelled.value) &&
          paymentsForDisplay.length > 0 && (
            <div className="crd-section">
              <p className="crd-section-title">{t(TRANSLATIONS_REQUESTS.details.deductionsCard.deductionPerPayroll)}</p>
              <div className="crd-payments-grid">
                {paymentsForDisplay.map((payment, index) => (
                  <div
                    key={`${payment.month}-${index}`}
                    className={`crd-payment-item status-${payment.displayStatus}`}
                    data-status={payment.displayStatus}
                    data-testid="crd-payment-item"
                  >
                    <div className="crd-payment-icon">
                      {payment.displayStatus === 'paid' && <CheckIcon className="crd-icon paid" />}
                      {payment.displayStatus === 'toPay' && <ClockIcon className="crd-icon to-pay" />}
                    </div>
                    <div className="crd-payment-info">
                      <p className="month">{getLocalizedMonthLabel(payment.month)}</p>
                      <p className="amount">{formatCurrency(payment.amount)}</p>
                    </div>
                  </div>
                ))}
                {fillRows()}
              </div>
            </div>
          )}
      </div>
      {request.status === FLEXIBLE_REMUNERATION_REQUEST_STATUS.active.value && paymentsForDisplay.length > 0 && (
        <div className="flex justify-end">
          <BdsButton variant="secondary" size="m" action={onCancel} iconLeft={XERROR_ICON}>
            {t(TRANSLATIONS_REQUESTS.details.deductionsCard.cancelButton)}
          </BdsButton>
        </div>
      )}
    </div>
  );
};

export default CourseRequestDiscounts;
